import { useState } from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { DateTime } from "luxon";
import { Time } from "@internationalized/date";
import type { TimeValue } from "react-aria-components";
import { DateInput, DateSegment, Dialog, DialogTrigger, TimeField } from "react-aria-components";

import { InlineEditableTrigger } from "./inline-editable-trigger";
import { CreatedDateCap, resolveCreatedToast } from "./resolve-created-toast";

import { INoteObservable } from "@/store/note";
import { generateShortTimeString } from "@/domains/date/date";
import { toIsoWithOffset } from "@/modules/date-time/locallyCreatedAt";
import { toastModule } from "@/modules/toast";
import { MDSPopover } from "@/mds/components/overlays/popover";
import { MDSTooltip, MDSTooltipPlacement } from "@/mds/components/tooltip";
import { ZIndex } from "@/domains/design/constants";
import { TrackedEvent, trackEvent } from "@/domains/metrics";

interface Props {
  note: INoteObservable;
}

const luxonToTime = (iso: string): Time | null => {
  const dt = DateTime.fromISO(iso);
  if (!dt.isValid) return null;
  return new Time(dt.hour, dt.minute);
};

// The epoch floor mirrors the date popover's MIN_DATE — the time popover combines
// the existing date with a new time-of-day, which can land before 1970 on epoch-day
// notes if the new time pushes the instant below zero. Compare in instants, not
// time-of-day, for the same reason as clampDateTimeToUpperBound.
const EPOCH = DateTime.fromMillis(0, { zone: "utc" });

export const clampDateTimeToUpperBound = (proposed: DateTime, cap: DateTime): DateTime =>
  proposed.toMillis() > cap.toMillis() ? cap : proposed;

export const clampDateTimeToLowerBound = (proposed: DateTime, floor: DateTime): DateTime =>
  proposed.toMillis() < floor.toMillis() ? floor : proposed;

export const EditCreatedTimePopoverTrigger = observer(function EditCreatedTimePopoverTrigger({
  note,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [draftTime, setDraftTime] = useState<TimeValue | null>(luxonToTime(note.locallyCreatedAt));

  const dt = DateTime.fromISO(note.locallyCreatedAt);
  const label = generateShortTimeString(dt);

  const handleOpenChange = (next: boolean) => {
    if (next) {
      setDraftTime(luxonToTime(note.locallyCreatedAt));
      trackEvent(TrackedEvent.EditNoteCreatedTimeOpen, { note_id: note.id });
    }
    setIsOpen(next);
  };

  const submit = async () => {
    if (!draftTime) return;
    const previousIso = note.locallyCreatedAt;
    const existing = DateTime.fromISO(previousIso);
    if (!existing.isValid) return;
    const proposed = existing.set({ hour: draftTime.hour, minute: draftTime.minute });
    // Snap any commit past the immutable insertion timestamp down to the cap, and
    // any commit before the epoch up to the floor. Lower clamp runs after upper so
    // a pathological cap < epoch still resolves to a valid in-range instant.
    const upperCap = DateTime.fromISO(note.quotaCreatedAt);
    const upperBounded = upperCap.isValid
      ? clampDateTimeToUpperBound(proposed, upperCap)
      : proposed;
    const next = clampDateTimeToLowerBound(upperBounded, EPOCH);
    const upperCapped =
      next.toMillis() !== proposed.toMillis() && next.toMillis() < proposed.toMillis();
    const lowerCapped =
      next.toMillis() !== proposed.toMillis() && next.toMillis() > proposed.toMillis();
    const cap: CreatedDateCap = upperCapped ? "upper" : lowerCapped ? "lower" : "none";
    const nextIso = toIsoWithOffset(next);
    setIsOpen(false);
    trackEvent(TrackedEvent.EditNoteCreatedTimeCommit, {
      note_id: note.id,
      previous_locally_created_at: previousIso,
      next_locally_created_at: nextIso,
    });
    await note.setCreatedAt(nextIso);
    toastModule.triggerToast({
      content: resolveCreatedToast({ surface: "time", cap, nextDt: next }),
    });
  };

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={handleOpenChange}>
      <MDSTooltip config={{ label: "Edit created time", placement: MDSTooltipPlacement.Top }}>
        <InlineEditableTrigger aria-label={`Edit created time — ${label}`}>
          {label}
        </InlineEditableTrigger>
      </MDSTooltip>
      <MDSPopover zIndex={ZIndex.ReactAriaPopover}>
        <Dialog>
          <PopoverContent
            onSubmit={e => {
              e.preventDefault();
              void submit();
            }}
          >
            <TimeField
              aria-label="Edit created time"
              value={draftTime}
              onChange={setDraftTime}
              onKeyDown={e => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void submit();
                }
              }}
            >
              <StyledDateInput>{segment => <StyledSegment segment={segment} />}</StyledDateInput>
            </TimeField>
          </PopoverContent>
        </Dialog>
      </MDSPopover>
    </DialogTrigger>
  );
});

const PopoverContent = styled.form`
  padding: ${({ theme }) => theme.spacing[1]};
  background: ${({ theme }) => theme.colors.background.menu.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
`;

const StyledDateInput = styled(DateInput)`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-width: 264px;
  height: 40px;
  font: inherit;
  padding: 0 ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.large};

  &[data-focus-within] {
    border-color: ${({ theme }) => theme.colors.border.emphasis};
  }
`;

const StyledSegment = styled(DateSegment)`
  padding: 0 ${({ theme }) => theme.spacing[0.5]};
  border-radius: ${({ theme }) => theme.borderRadius.small};

  &[data-type="literal"] {
    padding: 0;
  }

  &[data-focused] {
    background: ${({ theme }) => theme.colors.background.menu.hover};
    outline: none;
  }
`;
